/**
 * Licensed Materials - Property of IBM
 * IBM Cognos Products: Dashboard
 * (C) Copyright IBM Corp. 2019, 2021
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */
define([
	'underscore',
	'../../../lib/@waca/dashboard-common/dist/core/APIFactory',
	'../../../lib/@waca/dashboard-common/dist/api/PropertiesProviderAPI',
	'../../../api/CachingPropertiesProviderAPI',
	'../../../visualizations/vipr/properties/VIPRProperties',
	'../../../util/TransactionUtil'
], function (_, APIFactory, PropertiesProviderAPI, CachingPropertiesProviderAPI, VIPRProperties, TransactionUtil) {

	class ViprPropertyProvider {

		constructor(options) {
			this.content = options.content;
			this.dashboard = options.dashboardAPI;
			this.features = options.features;
			const { Properties } = options.features;
			Properties.registerProvider(this.getAPI());
			this.model = this.content.getFeature('Models.internal').getWidgetModel();
			this.content.on('change:visualization:type', this.onVisualizationChange, this);
			this.content.on('change:visualization:slots', this.onVisualizationChange, this);
			this.content.on('change:property', this.onVisualizationChange, this);
			this.dashboard.on('widget:maximize', this.onVisualizationChange, this);
			this.dashboard.on('widget:restore', this.onVisualizationChange, this);
			this.VisDefinitions = this.dashboard.getFeature('VisDefinitions');
			const featureChecker = this.dashboard.getGlassCoreSvc && this.dashboard.getGlassCoreSvc('.FeatureChecker');
			this.isPropertiesV2Enabled = featureChecker && featureChecker.checkValue('dashboard', 'PropertiesV2', 'enabled');
			this.VisDefinitions.on('refresh:definition', this.onVisualizationChange, this);
			this.visualization = options.features.Visualization;

			// TODO - livewidget_cleanup - figure a a cleaner way to clean the property list cache
			// When the slots or type change, during rendering, we will try to access the property , so if we reset it bebfore rendering,
			// then we will attempt to generate the property list during rendering and using the old slots mappings and we might get the wrong properties (those props that depend on slots like legend)
			this.content.on('change:state:status', this.onRenderStateChange, this);
			this._isChanged = true;
		}

		getAPI() {
			if (!this._api) {
				this._api = APIFactory.createAPI(this, [PropertiesProviderAPI, CachingPropertiesProviderAPI]);
			}
			return this._api;
		}

		destroy() {
			this.content.off('change:state:status', this.onRenderStateChange, this);
			this.content.off('change:type', this.onVisualizationChange, this);
			this.content.off('change:property', this.onVisualizationChange, this);
			this.dashboard.off('widget:maximize', this.onVisualizationChange, this);
			this.dashboard.off('widget:restore', this.onVisualizationChange, this);
			this.VisDefinitions.off('refresh:definition', this.onVisualizationChange, this);
			this.content = null;
		}

		reset() {
			this._isChanged = false;
			this._propertyList = null;
			this._propertyLayoutList = null;
		}

		onRenderStateChange(event) {
			if (this._isChanged && event.info.value === 'rendered') {
				this.contentRendered = true;
				this.reset();
			}
		}

		onVisualizationChange(event) {
			this._isChanged = true;
			if(event.name === 'change:visualization:type') {
				this.contentRendered = false;
			}
		}

		_getLivewidgetInternal() {
			if(!this.livewidgetInternal) {
				this.livewidgetInternal = this.content.getFeature('livewidget.internal');
			}
			return this.livewidgetInternal;
		}

		_getView() {
			const lwInternal = this._getLivewidgetInternal();
			return lwInternal && lwInternal.getVisView();
		}

		_isVIPR() {
			const renderer = this.visualization.getDefinition().getProperty('renderer');
			return renderer && renderer.indexOf('VIPRView') !== -1;
		}

		/**
		 * @param {*} property
		 * @param {*} widget
		 */
		modifyPropertyWithSetterAndGetter(property) {
			// [livewidget-cleanup] todo: removed the dependenty from widget
			const propertySupport = this.content.getFeature('Visualization.legacy').getLegacyManagers().visPropertySupport;
			_.extend(property, {
				getPropertyValue: () => propertySupport.getPropertyValue(property.id),
				setPropertyValue: (value, transactionToken) => {
					this.updateProperty({
						id: property.id,
						value: value
					}, transactionToken);
				},
				supportsUndoRedo: true
			});
		}

		/**
		 * @returns true if the property is not a 'general' property (i.e. is a
		 * visualization property that can be overriden by a user)
		 */
		isAnOverridableVisProperty(property) {
			var commonProps = {
				'fillColor': true,
				'borderColor': true,
				'transparency': true,
				'showTitle': true,
				'saveTitle': true,
				'queryRefresh': true
			};
			return !commonProps[property];
		}

		updateProperty(property, transactionToken) {
			if (this.isAnOverridableVisProperty(property.id)) {
				const boardModel = this.dashboard.getFeature('internal').getBoardModel();
				const options = TransactionUtil.transactionTokenToOptions(transactionToken);
				const extendedOptions = _.extend({}, options, boardModel.getLanguageModelOptions(true));

				const widgetModel = this.content.getFeature('Models.internal').getWidgetModel();
				if(property.id.indexOf('colorPalette') > -1) {
					if(this.features.LiveWidgetSegment) {
						this.features.LiveWidgetSegment.track({
							category: 'colorPalette',
							visualization: this.content.getFeature('Visualization'),
							content: this.content,
							info: {
								propertyId: property.id,
								previousValue: this.content.getPropertyValue(property.id),
								newValue: property.value
							}
						});
					}
				}

				if (property.value === undefined) {
					// Collection::set does not correctly merge undefined values into
					// the existing property, so do so manually.
					const originalProperty = widgetModel.properties.get(property.id);
					originalProperty.set({
						value: undefined
					}, _.extend({
						merge: true,
						remove: false,
						silent: true
					}, extendedOptions));
				} else {
					widgetModel.properties.set(property, _.extend({
						merge: true,
						remove: false,
						silent: true
					}, extendedOptions));
				}
			}
		}

		getPropertyList() {
			if (!this._propertyList && this._isVIPR()) {
				let rawPropertyList;
				let visView = this._getView();
				if (visView && this.contentRendered) {
					rawPropertyList = visView.getPropertyList();
				} else {
					const slots = this.visualization.getSlots().getSlotList();
					const properties = this.visualization.getDefinition().getProperty('vizDefProperties');
					rawPropertyList = VIPRProperties.getInstance().getPropertyList(
						properties,
						slots,
						this.content,
						this.isPropertiesV2Enabled
					);
				}
				if (rawPropertyList) {
					let propertyList =  _.map(rawPropertyList, (property) => {
						const newProp = _.extend({}, property);
						this.modifyPropertyWithSetterAndGetter(newProp);

						// If the property is tagged as multilingual and we're in translation mode and we're missing the translation for the current locale
						if (newProp.editor && newProp.editor.uiControl && newProp.editor.uiControl.multilingual) {
							const translationService = this.dashboard.getFeature('TranslationService');
							if (translationService) {
								const { multilingualProperty } = this.model.getMultilingualAttribute(newProp.id) || {};
								translationService.processMultilingualProperty(newProp, multilingualProperty);
							}
						}
						return newProp;
					});
					this._propertyList = propertyList;
				}
			}
			return this._propertyList;
		}

		getPropertyLayoutList() {
			if (!this._propertyLayoutList && this._isVIPR()) {
				this._propertyLayoutList = [];
				const visView = this._getView();
				if (visView && this.contentRendered) {
					this._propertyLayoutList = visView.getPropertyLayoutList();
				} else {
					const slots = this.visualization.getSlots().getSlotList();
					const properties = this.visualization.getDefinition().getProperty('vizDefProperties');
					this._propertyLayoutList = VIPRProperties.getInstance().getPropertyLayoutList(
						properties,
						slots,
						this.content,
						this.dashboard,
						this.isPropertiesV2Enabled
					);
				}
			}
			return this._propertyLayoutList;
		}
	}

	return ViprPropertyProvider;
});
